import { apiClient } from './corezoidApi';
import getApiBaseUrl from '../utils/apiBaseUrl';

const API_BASE_URL = getApiBaseUrl();

export const fetchDashboardOrderSummary = async (dateFrom, dateTo) => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/data/dashboard/order-summary`, {
      params: { dateFrom, dateTo }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to load dashboard order summary');
  }
};

export const fetchDashboardWalletBalance = async () => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/data/dashboard/wallet-balance`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to load wallet balance');
  }
};

/**
 * Loads PayIN data
 * @param {string} status - Status: 'all', 'pending', 'completed', 'cancelled'
 * @param {number} page - Page number (starting from 1)
 * @param {number} limit - Items per page
 */
export const fetchPayINData = async (
  status = 'all', page = 1, limit = 50, search = '', dateFrom = '', dateTo = '', filters = {}
) => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/data/payin`, {
      params: { status, page, limit, search, dateFrom, dateTo, filters: JSON.stringify(filters || {}) }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to load PayIN data');
  }
};

/**
 * Loads PayOUT data
 * @param {string} status - Status: 'all', 'pending', 'completed', 'cancelled'
 * @param {number} page - Page number (starting from 1)
 * @param {number} limit - Items per page
 */
export const fetchPayOUTData = async (
  status = 'all', page = 1, limit = 50, search = '', dateFrom = '', dateTo = '', filters = {}
) => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/data/payout`, {
      params: { status, page, limit, search, dateFrom, dateTo, filters: JSON.stringify(filters || {}) }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to load PayOUT data');
  }
};

export const fetchOrderFilterOptions = async (pageType) => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/data/${pageType}/filter-options`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to load order filter options');
  }
};

export const fetchBalanceOperations = async (page = 1, limit = 50, search = '') => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/data/balance-operations`, { params: { page, limit, search } });
    return response.data;
  } catch (error) { throw new Error(error.response?.data?.error || 'Failed to load balance operations'); }
};

const getOrderId = (order) => {
  const orderData = order?.data || order;
  return orderData?.OrderId || orderData?.orderId || orderData?.order_id || null;
};

/**
 * Loads one order by its exact OrderId without refreshing the current table.
 */
export const fetchOrderById = async (pageType, orderId) => {
  const response = await apiClient.get(`${API_BASE_URL}/data/${pageType}`, {
    params: { status: 'all', page: 1, limit: 50, search: String(orderId), fresh: 'true' }
  });
  const result = response.data;
  const records = Array.isArray(result?.data) ? result.data : [];

  return records.find(
    (order) => String(getOrderId(order)) === String(orderId)
  ) || null;
};

/**
 * Subscribes to realtime order status events. Uses fetch streaming so the JWT
 * can stay in the Authorization header instead of being exposed in the URL.
 */
export const subscribeToOrderEvents = (onEvent, { onReconnect } = {}) => {
  let stopped = false;
  let controller = null;
  let reconnectTimer = null;
  let hasConnected = false;

  const connect = async () => {
    const token = localStorage.getItem('token');
    if (stopped || !token) {
      return;
    }

    controller = new AbortController();

    try {
      const response = await fetch(`${API_BASE_URL}/events/orders`, {
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`
        },
        signal: controller.signal
      });

      if (!response.ok || !response.body) {
        throw new Error(`Order events connection failed: ${response.status}`);
      }

      if (hasConnected) {
        onReconnect?.();
      }
      hasConnected = true;

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (!stopped) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
        const messages = buffer.split('\n\n');
        buffer = messages.pop() || '';

        messages.forEach((message) => {
          const dataLine = message
            .split('\n')
            .find((line) => line.startsWith('data:'));
          if (!dataLine) return;

          try {
            onEvent(JSON.parse(dataLine.slice(5).trim()));
          } catch (error) {
            console.error('Failed to parse order status event:', error);
          }
        });
      }
    } catch (error) {
      if (!stopped && error.name !== 'AbortError') {
        console.error('Order events connection lost:', error);
      }
    }

    if (!stopped) {
      reconnectTimer = window.setTimeout(connect, 3000);
    }
  };

  connect();

  return () => {
    stopped = true;
    controller?.abort();
    if (reconnectTimer) {
      window.clearTimeout(reconnectTimer);
    }
  };
};

/**
 * Loads Requisites data
 * @param {number} page - Page number (starting from 1)
 * @param {number} limit - Items per page
 */
export const fetchRequisitesData = async (page = 1, limit = 50, search = '') => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/data/requisites`, {
      params: { page, limit, search }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to load Requisites data');
  }
};

/**
 * Loads Devices data
 * @param {number} page - Page number (starting from 1)
 * @param {number} limit - Items per page
 */
export const fetchDevicesData = async (page = 1, limit = 50, search = '') => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/data/devices`, {
      params: { page, limit, search }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to load Devices data');
  }
};

/**
 * Loads Traders data (admin only)
 * @param {number} page - Page number (starting from 1)
 * @param {number} limit - Items per page
 */
export const fetchTradersData = async (page = 1, limit = 50, search = '', role = 'all') => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/data/traders`, {
      params: { page, limit, search, role }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to load Traders data');
  }
};

/**
 * Loads Gateways data (admin only)
 * @param {number} page - Page number (starting from 1)
 * @param {number} limit - Items per page
 */
export const fetchGatewaysData = async (page = 1, limit = 50, search = '') => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/data/gateways`, {
      params: { page, limit, search }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to load Gateways data');
  }
};

/**
 * Loads Prices data (admin only)
 * @param {number} page - Page number (starting from 1)
 * @param {number} limit - Items per page
 */
export const fetchPricesData = async (page = 1, limit = 50, search = '') => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/data/prices`, {
      params: { page, limit, search }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to load Prices data');
  }
};

/**
 * Loads Banks data (admin only)
 * @param {number} page - Page number (starting from 1)
 * @param {number} limit - Items per page
 */
export const fetchBanksData = async (page = 1, limit = 50, search = '') => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/data/banks`, {
      params: { page, limit, search }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to load Banks data');
  }
};

/**
 * Loads Currencies data (admin only)
 * @param {number} page - Page number (starting from 1)
 * @param {number} limit - Items per page
 */
export const fetchCurrenciesData = async (page = 1, limit = 50, search = '') => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/data/currencies`, {
      params: { page, limit, search }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to load Currencies data');
  }
};

/**
 * Loads Messages data
 * @param {number} page - Page number (starting from 1)
 * @param {number} limit - Items per page
 */
export const fetchMessagesData = async (page = 1, limit = 50, search = '') => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/data/messages`, {
      params: { page, limit, search }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to load Messages data');
  }
};

/**
 * Loads Transactions data
 * @param {number} page - Page number (starting from 1)
 * @param {number} limit - Items per page
 */
export const fetchTransactionsData = async (page = 1, limit = 50, search = '') => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/data/transactions`, {
      params: { page, limit, search }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to load Transactions data');
  }
};

/** Loads Requests data (admin only). */
export const fetchRequestsData = async (page = 1, limit = 50, search = '') => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/data/requests`, {
      params: { page, limit, search }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to load Requests data');
  }
};

/**
 * Loads current trader wallet
 */
export const fetchWalletData = async () => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/auth/wallet`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to load Wallet data');
  }
};

/**
 * Starts device registration (step 1)
 * @param {Object} params
 * @param {string} params.deviceId
 * @param {string} params.userDeviceName
 */
export const startDeviceRegistration = async ({ deviceId, userDeviceName }) => {
  try {
    const response = await apiClient.post(`${API_BASE_URL}/device/register/start`, {
      device_id: deviceId,
      user_device_name: userDeviceName
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to start device registration');
  }
};

/**
 * Checks device registration status
 * @param {string} deviceId
 */
export const getDeviceRegistrationStatus = async (deviceId) => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/device/register/status`, {
      params: { device_id: deviceId }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to check registration status');
  }
};

/**
 * Creates a new trader (admin only)
 * @param {Object} traderData - Trader data (login, password, name, role, status)
 */
export const createTrader = async (traderData) => {
  try {
    const response = await apiClient.post(`${API_BASE_URL}/traders/create`, traderData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create trader');
  }
};

/**
 * Updates an existing trader (admin only)
 * @param {Object} traderData - Updated trader data
 * @param {Object} originalTrader - Original trader data
 */
export const updateTrader = async (traderData, originalTrader) => {
  try {
    const response = await apiClient.post(`${API_BASE_URL}/traders/update`, {
      traderData,
      originalTrader
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to update trader');
  }
};

/**
 * Creates a balance add request for a trader (admin only)
 * @param {Object} payload
 */
export const createTraderBalanceRequest = async (payload) => {
  try {
    const response = await apiClient.post(`${API_BASE_URL}/traders/balance/create`, payload);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create balance request');
  }
};

/**
 * Creates a balance add request for a gateway (admin only)
 * @param {Object} payload
 */
export const createGatewayBalanceRequest = async (payload) => {
  try {
    const response = await apiClient.post(`${API_BASE_URL}/gateways/balance/create`, payload);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create balance request');
  }
};
